import type { ComponentType, JSX, PropsWithChildren } from 'react';

import { TrackingContext } from '@change-corgi/core/react/tracking';

import { useQueryParamValue } from 'src/app/shared/hooks/location';
import type { ErrorStateDetails } from 'src/app/shared/utils/async';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import type {
	PetitionDetailsPageCombinedState,
	PetitionDetailsPagePrefetchedContext,
	PetitionDetailsPagePrefetchedUserContext,
} from './hook';
import { usePageContext } from './hook';
import { PetitionDetailsPageDataContextProvider } from './pageData';
import { PetitionDetailsPageStateContextProvider } from './pageState';
import { PetitionDetailsPageUserDataContextProvider } from './pageUserData';

export type ErrorHandlerProps = {
	context: ErrorStateDetails<PetitionDetailsPageCombinedState> | { error: 'user_info_load_failure'; cause?: unknown };
};

type Props = {
	slug: string;
	prefetchedData: PetitionDetailsPagePrefetchedContext | undefined;
	prefetchedUserData: PetitionDetailsPagePrefetchedUserContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export function useTrackingExperimentData() {
	const utmContent = useQueryParamValue('utm_content');
	if (!utmContent || !/^fbogexp:\w+:\w+$/.test(utmContent)) return undefined;
	const [, experimentName, variantName] = utmContent.split(':');
	return {
		fbogexp_experiment_name: experimentName,
		fbogexp_variant_name: variantName,
	};
}

export function PetitionDetailsPageContextProvider({
	children,
	slug,
	prefetchedData,
	prefetchedUserData,
	errorHandler: ErrorHandler,
	loadingFallback,
}: PropsWithChildren<Props>): JSX.Element | null {
	const pageContextState = usePageContext(slug, prefetchedData, prefetchedUserData);
	const experimentTrackingData = useTrackingExperimentData();

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData, pageUserData } = pageContextState;

	return (
		<PetitionDetailsPageDataContextProvider pageData={pageData}>
			<PetitionDetailsPageUserDataContextProvider pageUserData={pageUserData}>
				<PetitionDetailsPageStateContextProvider>
					<TrackingContext properties={{ ...experimentTrackingData }}>{children}</TrackingContext>
				</PetitionDetailsPageStateContextProvider>
			</PetitionDetailsPageUserDataContextProvider>
		</PetitionDetailsPageDataContextProvider>
	);
}
